import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Header: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
  };

  return (
    <header className="bg-white shadow-sm border-b border-stone-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/logos/Logo_name-orange.png"
              alt="Iza Łysoń Dog Photography"
              className="h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav aria-label="Nawigacja główna" className="hidden md:flex space-x-8">
            <Link
              to="/courses"
              className="text-stone-700 hover:text-brand-600 transition-colors font-medium"
            >
              Kursy
            </Link>
            <Link
              to="/workshops"
              className="text-stone-700 hover:text-brand-600 transition-colors font-medium"
            >
              Warsztaty
            </Link>
            <Link
              to="/competitions"
              className="text-stone-700 hover:text-brand-600 transition-colors font-medium"
            >
              Konkursy
            </Link>
            <Link
              to="/newsletter"
              className="text-stone-700 hover:text-brand-600 transition-colors font-medium"
            >
              Newsletter
            </Link>
            <Link
              to="/portfolio"
              className="text-stone-700 hover:text-brand-600 transition-colors font-medium"
            >
              Portfolio
            </Link>
          </nav>

          {/* Desktop Auth Section */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  aria-expanded={isMenuOpen}
                  aria-haspopup="true"
                  className="flex items-center space-x-2 text-stone-700 hover:text-brand-600"
                >
                  <div className="w-8 h-8 bg-brand-500 rounded-full flex items-center justify-center text-white text-sm font-medium" aria-hidden="true">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </div>
                  <span className="text-sm font-medium">
                    {user?.firstName} {user?.lastName}
                  </span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isMenuOpen && (
                  <div role="menu" className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50 border border-stone-100">
                    <Link
                      to="/dashboard"
                      role="menuitem"
                      className="block px-4 py-2 text-sm text-stone-700 hover:bg-brand-50 hover:text-brand-700"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Panel
                    </Link>
                    <Link
                      to="/profile"
                      role="menuitem"
                      className="block px-4 py-2 text-sm text-stone-700 hover:bg-brand-50 hover:text-brand-700"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Profil
                    </Link>
                    <button
                      onClick={handleLogout}
                      role="menuitem"
                      className="block w-full text-left px-4 py-2 text-sm text-stone-700 hover:bg-brand-50 hover:text-brand-700"
                    >
                      Wyloguj
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-stone-700 hover:text-brand-600 transition-colors font-medium"
                >
                  Zaloguj
                </Link>
                <Link
                  to="/register"
                  className="bg-brand-500 text-white px-5 py-2 rounded-lg hover:bg-brand-600 transition-colors font-medium"
                >
                  Rejestracja
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden flex items-center text-stone-700"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-label={isMenuOpen ? 'Zamknij menu' : 'Otwórz menu'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav aria-label="Menu mobilne" className="md:hidden pb-4">
            <div className="flex flex-col space-y-2">
              <Link
                to="/courses"
                className="text-stone-700 hover:text-brand-600 py-2 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Kursy
              </Link>
              <Link
                to="/workshops"
                className="text-stone-700 hover:text-brand-600 py-2 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Warsztaty
              </Link>
              <Link
                to="/competitions"
                className="text-stone-700 hover:text-brand-600 py-2 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Konkursy
              </Link>
              <Link
                to="/newsletter"
                className="text-stone-700 hover:text-brand-600 py-2 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Newsletter
              </Link>
              <Link
                to="/portfolio"
                className="text-stone-700 hover:text-brand-600 py-2 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Portfolio
              </Link>

              {isAuthenticated ? (
                <>
                  <div className="border-t border-stone-200 pt-2 mt-2">
                    <Link
                      to="/dashboard"
                      className="block text-stone-700 hover:text-brand-600 py-2 font-medium"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Panel
                    </Link>
                    <Link
                      to="/profile"
                      className="block text-stone-700 hover:text-brand-600 py-2 font-medium"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Profil
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left text-stone-700 hover:text-brand-600 py-2 font-medium"
                    >
                      Wyloguj
                    </button>
                  </div>
                </>
              ) : (
                <div className="border-t border-stone-200 pt-2 mt-2 space-y-2">
                  <Link
                    to="/login"
                    className="block text-stone-700 hover:text-brand-600 py-2 font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Zaloguj
                  </Link>
                  <Link
                    to="/register"
                    className="block bg-brand-500 text-white px-4 py-2 rounded-lg hover:bg-brand-600 text-center font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Rejestracja
                  </Link>
                </div>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
